/**
 * calendar.tsx — Calendar shadcn sobre react-day-picker (API v9/v10: <DayPicker />).
 * Estética FORJA sobre tema papel (superficies claras): día seleccionado en ámbar
 * (brand-accent), hoy con ring sutil, navegación con botones ghost, labels mono.
 * Autocontenido: NO importa nada de features. Locale es (date-fns) → meses/días en español.
 *
 * El estilo se aplica 100% vía `classNames` (no se importa el CSS por defecto de
 * react-day-picker) para mantener todo token-driven.
 */
import * as React from "react";
import { DayPicker, type ChevronProps } from "react-day-picker";
import { es } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/shared/lib/utils.js";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/** Chevron de navegación: usa los iconos de lucide según orientación. */
function Chevron({ orientation, className }: ChevronProps) {
  const Icon = orientation === "right" ? ChevronRight : ChevronLeft;
  return <Icon className={cn("h-4 w-4", className)} aria-hidden />;
}

export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={es}
      className={cn("select-none", className)}
      formatters={{
        // Etiquetas de día de la semana abreviadas (lu, ma, mi…) para el look editorial.
        formatWeekdayName: (date, options) => {
          const locale = options?.locale ?? es;
          return locale.localize.day(date.getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6, {
            width: "short",
          }).slice(0, 2);
        },
      }}
      classNames={{
        months: "flex flex-col gap-4",
        month: "relative flex flex-col gap-3",
        month_caption: "flex h-8 items-center justify-center",
        caption_label:
          "font-mono text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-foreground",
        nav: "absolute inset-x-0 top-0 flex items-center justify-between px-1",
        button_previous:
          "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
        button_next:
          "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "w-9 font-mono text-[0.5625rem] font-medium uppercase tracking-[0.12em] text-muted-foreground",
        week: "mt-1 flex w-full",
        day: "h-9 w-9 p-0 text-center text-sm",
        day_button:
          "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-normal text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected:
          "[&>button]:bg-brand-accent [&>button]:text-brand-accent-foreground [&>button]:font-medium [&>button:hover]:bg-brand-accent",
        today: "[&>button]:ring-1 [&>button]:ring-border",
        outside: "[&>button]:text-muted-foreground/40",
        disabled: "[&>button]:opacity-40 [&>button]:pointer-events-none",
        hidden: "invisible",
        ...classNames,
      }}
      components={{ Chevron }}
      {...props}
    />
  );
}
